import {
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Controls,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Grip, Hand, MousePointer2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { estimateTableHeight } from '@hyphen-db-studio/diagram-engine';
import type { RefType } from '@hyphen-db-studio/schema-core';
import {
  fieldSteps,
  groupSteps,
  relationshipSteps,
  tableSteps,
  type Step,
} from '../../lib/explanationRefs';
import ExplanationContextMenu from '../workspace/ExplanationContextMenu';
import ErTourOverlay from './ErTourOverlay';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useSchemaStore } from '../../store/useSchemaStore';
import { useThemeStore } from '../../store/useThemeStore';
import GroupNode from './GroupNode';
import RelationshipEdge from './RelationshipEdge';
import TableNode from './TableNode';

const NODE_WIDTH = 240;
const GROUP_PREFIX = 'group:';
// Note: the React Flow node "type" is intentionally NOT "group" — that string collides
// with a reserved class (.react-flow__node-group) that ships a default 1px dark border
// and selection box-shadow in @xyflow/react's base stylesheet, which showed through as an
// unwanted solid outline no matter what we styled in our own component.
const nodeTypes = { table: TableNode, domainGroup: GroupNode };
const edgeTypes = { relationship: RelationshipEdge };

function computeForeignKeyColumnIds(relationships: ReturnType<typeof useSchemaStore.getState>['schema']['relationships']): Set<string> {
  const fk = new Set<string>();
  for (const rel of relationships) {
    if (rel.type === '<') fk.add(rel.toColumnId);
    else fk.add(rel.fromColumnId);
  }
  return fk;
}

function InteractionModeToggle() {
  const interactionMode = useDiagramViewStore((s) => s.interactionMode);
  const setInteractionMode = useDiagramViewStore((s) => s.setInteractionMode);
  const showGrid = useDiagramViewStore((s) => s.showGrid);
  const toggleGrid = useDiagramViewStore((s) => s.toggleGrid);

  return (
    <Panel position="top-left">
      <div className="flex items-center gap-0.5 rounded-lg border border-black/10 bg-panel p-1 shadow-md dark:border-white/10 dark:bg-gray-800">
        <button
          onClick={() => setInteractionMode('select')}
          title="Select mode: drag an element to move it, drag empty canvas to pan"
          className={`rounded-md p-1.5 ${
            interactionMode === 'select' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          <MousePointer2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => setInteractionMode('pan')}
          title="Pan mode: drag anywhere to move the canvas"
          className={`rounded-md p-1.5 ${
            interactionMode === 'pan' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          <Hand className="h-4 w-4" />
        </button>
        <div className="mx-0.5 h-5 w-px bg-gray-200 dark:bg-white/10" />
        <button
          onClick={toggleGrid}
          title={showGrid ? 'Hide dot grid' : 'Show dot grid'}
          className={`rounded-md p-1.5 ${
            showGrid ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300' : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
          }`}
        >
          <Grip className="h-4 w-4" />
        </button>
      </div>
    </Panel>
  );
}

function ToastPanel() {
  const toast = useDiagramViewStore((s) => s.toast);
  if (!toast) return null;
  return (
    <Panel position="top-center">
      <div className="rounded-md bg-gray-900/90 px-3 py-1.5 text-xs font-medium text-white shadow-lg dark:bg-gray-100/90 dark:text-gray-900">
        {toast}
      </div>
    </Panel>
  );
}

function FlowInner({ containerRef }: { containerRef: React.RefObject<HTMLDivElement> }) {
  const tables = useSchemaStore((s) => s.schema.tables);
  const groups = useSchemaStore((s) => s.schema.groups);
  const relationships = useSchemaStore((s) => s.schema.relationships);
  const selection = useSchemaStore((s) => s.selection);
  const setSelection = useSchemaStore((s) => s.setSelection);
  const setTablePosition = useSchemaStore((s) => s.setTablePosition);
  const setTablePositions = useSchemaStore((s) => s.setTablePositions);
  const addRelationship = useSchemaStore((s) => s.addRelationship);
  const autoLayout = useSchemaStore((s) => s.autoLayout);
  const reactFlow = useReactFlow();
  const setInstance = useDiagramViewStore((s) => s.setInstance);
  const interactionMode = useDiagramViewStore((s) => s.interactionMode);
  const showToast = useDiagramViewStore((s) => s.showToast);
  const multiSelectedIds = useDiagramViewStore((s) => s.multiSelectedIds);
  const setMultiSelectedIds = useDiagramViewStore((s) => s.setMultiSelectedIds);
  const multiSet = useMemo(() => new Set(multiSelectedIds), [multiSelectedIds]);
  const forceRenderAll = useDiagramViewStore((s) => s.forceRenderAll);
  const showGrid = useDiagramViewStore((s) => s.showGrid);
  const readOnly = useDiagramViewStore((s) => s.readOnly);
  const documentation = useSchemaStore((s) => s.documentation);
  const tourOpen = useDiagramViewStore((s) => s.tourOpen);
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; steps: Step[] } | null>(null);
  const theme = useThemeStore((s) => s.theme);
  const didAutoLayout = useRef(false);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (didAutoLayout.current) return;
    if (tables.length > 0 && tables.some((t) => !t.position)) {
      didAutoLayout.current = true;
      autoLayout();
    }
  }, [tables, autoLayout]);

  const fkColumnIds = useMemo(() => computeForeignKeyColumnIds(relationships), [relationships]);
  const selectedTableId = selection?.type === 'table' ? selection.id : null;
  const selectedGroupId = selection?.type === 'group' ? selection.id : null;

  // Highlight every relationship touching the selected table, or — when a group is
  // selected instead — every relationship touching any of that group's member tables
  // (both the ones crossing out to other groups and the ones between two of its own
  // members), so selecting a domain lights up all of its connectors at once.
  const connectedEdgeIds = useMemo(() => {
    let memberIds: Set<string> | null = null;
    if (selectedTableId) {
      memberIds = new Set([selectedTableId]);
    } else if (selectedGroupId) {
      memberIds = new Set(tables.filter((t) => t.groupId === selectedGroupId).map((t) => t.id));
    }
    if (!memberIds || memberIds.size === 0) return null;
    const ids = new Set<string>();
    for (const rel of relationships) {
      if (memberIds.has(rel.fromTableId) || memberIds.has(rel.toTableId)) ids.add(rel.id);
    }
    return ids;
  }, [relationships, selectedTableId, selectedGroupId, tables]);

  const groupColorById = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groups) if (group.color) map.set(group.id, group.color);
    return map;
  }, [groups]);

  const tableNodes: Node[] = useMemo(
    () =>
      tables.map((table) => ({
        id: table.id,
        type: 'table',
        position: table.position ?? { x: 0, y: 0 },
        data: {
          name: table.name,
          columns: table.columns,
          color: table.color,
          groupColor: table.groupId ? groupColorById.get(table.groupId) : undefined,
          isSelected: selectedTableId === table.id || multiSet.has(table.id),
          fkColumnIds,
        },
        selected: selectedTableId === table.id || multiSet.has(table.id),
        zIndex: 1,
      })),
    [tables, selectedTableId, multiSet, fkColumnIds, groupColorById],
  );

  const groupNodes: Node[] = useMemo(
    () =>
      groups
        .map((group) => {
          const members = tables.filter((t) => t.groupId === group.id && t.position);
          if (!members.length) return null;
          const minX = Math.min(...members.map((t) => t.position!.x));
          const minY = Math.min(...members.map((t) => t.position!.y));
          const maxX = Math.max(...members.map((t) => t.position!.x + NODE_WIDTH));
          const maxY = Math.max(...members.map((t) => t.position!.y + estimateTableHeight(t.columns.length)));
          const pad = 28;
          const node: Node = {
            id: `${GROUP_PREFIX}${group.id}`,
            type: 'domainGroup',
            position: { x: minX - pad, y: minY - pad - 24 },
            style: { width: maxX - minX + pad * 2, height: maxY - minY + pad * 2 + 24 },
            data: { name: group.name, color: group.color, isSelected: selectedGroupId === group.id },
            selected: selectedGroupId === group.id || multiSet.has(`${GROUP_PREFIX}${group.id}`),
            selectable: true,
            zIndex: -1,
          };
          return node;
        })
        .filter((n): n is Node => n !== null),
    [groups, tables, selectedGroupId, multiSet],
  );

  const computedNodes = useMemo(() => [...groupNodes, ...tableNodes], [groupNodes, tableNodes]);

  // Local, live-rendered node state. React Flow needs onNodesChange to apply position
  // updates frame-by-frame during a drag; without it the node only "jumps" once we write
  // the final position back to the schema store on drag-stop. We resync from the
  // schema-derived nodes whenever they change, except mid-drag (where the local state is
  // the source of truth for the node actively being moved).
  const [renderNodes, setRenderNodes] = useState<Node[]>(computedNodes);
  useEffect(() => {
    if (isDraggingRef.current) return;
    setRenderNodes(computedNodes);
  }, [computedNodes]);

  // Dragging a group must carry its member tables along with it. Groups have no
  // stored position of their own (their box is derived from member positions), so we
  // detect the group's position delta each frame and apply the same delta to its
  // members' rendered positions — the final positions are written back to the schema
  // for every member at once in handleNodeDragStop.
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setRenderNodes((nds) => {
        let next = applyNodeChanges(changes, nds);
        for (const change of changes) {
          if (change.type !== 'position' || !change.position) continue;
          const prevNode = nds.find((n) => n.id === change.id);
          if (!prevNode || prevNode.type !== 'domainGroup') continue;
          const dx = change.position.x - prevNode.position.x;
          const dy = change.position.y - prevNode.position.y;
          if (!dx && !dy) continue;
          const groupId = change.id.slice(GROUP_PREFIX.length);
          const movedByChange = new Set(changes.filter((c) => c.type === 'position').map((c) => (c as { id: string }).id));
          const memberIds = new Set(
            tables.filter((t) => t.groupId === groupId && !movedByChange.has(t.id)).map((t) => t.id),
          );
          next = next.map((n) =>
            memberIds.has(n.id) ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } } : n,
          );
        }
        return next;
      });
    },
    [tables],
  );

  const renderNodesRef = useRef(renderNodes);
  useEffect(() => {
    renderNodesRef.current = renderNodes;
  }, [renderNodes]);

  const edges: Edge[] = useMemo(
    () =>
      relationships.map((rel) => ({
        id: rel.id,
        type: 'relationship',
        source: rel.fromTableId,
        target: rel.toTableId,
        sourceHandle: rel.fromColumnId,
        targetHandle: rel.toColumnId,
        data: { type: rel.type, highlighted: connectedEdgeIds?.has(rel.id) ?? false },
        selected: selection?.type === 'relationship' && selection.id === rel.id,
        zIndex: connectedEdgeIds?.has(rel.id) ? 10 : 0,
      })),
    [relationships, selection, connectedEdgeIds],
  );

  useEffect(() => {
    if (selection?.type === 'group') {
      // Bring the whole domain into view.
      const members = tables.filter((t) => t.groupId === selection.id && t.position);
      if (!members.length) return;
      reactFlow.fitView({ nodes: members.map((t) => ({ id: t.id })), padding: 0.2, maxZoom: 1, duration: 400 });
      return;
    }
    if (selection?.type !== 'table') return;
    const table = tables.find((t) => t.id === selection.id);
    if (!table?.position) return;
    const height = estimateTableHeight(table.columns.length);
    reactFlow.setCenter(table.position.x + NODE_WIDTH / 2, table.position.y + height / 2, {
      zoom: Math.max(reactFlow.getZoom(), 0.75),
      duration: 300,
    });
  }, [selection, tables, reactFlow]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target || !connection.sourceHandle || !connection.targetHandle) return;
      const defaultType: RefType = '>';
      addRelationship(connection.source, connection.sourceHandle, connection.target, connection.targetHandle, defaultType);
    },
    [addRelationship],
  );

  const blockedInPanMode = useCallback(() => {
    showToast('Switch to Select mode to select elements');
  }, [showToast]);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (interactionMode === 'pan') {
        blockedInPanMode();
        return;
      }
      if (node.type === 'table') setSelection({ type: 'table', id: node.id });
      else if (node.type === 'domainGroup') setSelection({ type: 'group', id: node.id.slice(GROUP_PREFIX.length) });
    },
    [interactionMode, blockedInPanMode, setSelection],
  );

  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      if (interactionMode === 'pan') {
        blockedInPanMode();
        return;
      }
      setSelection({ type: 'relationship', id: edge.id });
    },
    [interactionMode, blockedInPanMode, setSelection],
  );

  // Right-click on a table, one of its fields, a group or a relationship offers "Go to explanation".
  const handleNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      e.preventDefault();
      if (node.type === 'table') {
        const table = tables.find((t) => t.id === node.id);
        if (!table) return;
        const columnId = (e.target as HTMLElement).closest('[data-column-id]')?.getAttribute('data-column-id');
        const column = columnId ? table.columns.find((c) => c.id === columnId) : undefined;
        setMenu(
          column
            ? { x: e.clientX, y: e.clientY, title: `Field ${table.name}.${column.name}`, steps: fieldSteps(table.name, column.name) }
            : { x: e.clientX, y: e.clientY, title: `Table ${table.name}`, steps: tableSteps(table.name) },
        );
      } else if (node.type === 'domainGroup') {
        const group = groups.find((g) => g.id === node.id.slice(GROUP_PREFIX.length));
        if (group) setMenu({ x: e.clientX, y: e.clientY, title: `Group ${group.name}`, steps: groupSteps(group.name) });
      }
    },
    [tables, groups],
  );

  const handleEdgeContextMenu = useCallback(
    (e: React.MouseEvent, edge: Edge) => {
      e.preventDefault();
      const rel = relationships.find((r) => r.id === edge.id);
      const from = tables.find((t) => t.id === rel?.fromTableId);
      const to = tables.find((t) => t.id === rel?.toTableId);
      const fromColumn = from?.columns.find((c) => c.id === rel?.fromColumnId);
      const toColumn = to?.columns.find((c) => c.id === rel?.toColumnId);
      if (!from || !to || !fromColumn || !toColumn) return;
      setMenu({
        x: e.clientX,
        y: e.clientY,
        title: `Relationship ${from.name}.${fromColumn.name} → ${to.name}.${toColumn.name}`,
        steps: relationshipSteps({ table: from.name, column: fromColumn.name }, { table: to.name, column: toColumn.name }),
      });
    },
    [relationships, tables],
  );

  const handlePaneClick = useCallback(() => {
    setSelection(null);
    setMultiSelectedIds([]);
  }, [setSelection, setMultiSelectedIds]);

  // Marquee (or ctrl/cmd-click) selection: several nodes are highlighted and moved/deleted together, while a
  // single picked node behaves like a normal click and feeds the properties panel.
  const handleSelectionChange = useCallback(
    ({ nodes: picked }: { nodes: Node[] }) => {
      if (picked.length > 1) {
        setMultiSelectedIds(picked.map((n) => n.id));
        if (useSchemaStore.getState().selection) setSelection(null);
        return;
      }
      setMultiSelectedIds([]);
      const only = picked[0];
      if (!only) return;
      const current = useSchemaStore.getState().selection;
      if (only.type === 'table' && !(current?.type === 'table' && current.id === only.id)) {
        setSelection({ type: 'table', id: only.id });
      } else if (only.type === 'domainGroup') {
        const groupId = only.id.slice(GROUP_PREFIX.length);
        if (!(current?.type === 'group' && current.id === groupId)) setSelection({ type: 'group', id: groupId });
      }
    },
    [setSelection, setMultiSelectedIds],
  );

  const handleNodeDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleNodeDragStop = useCallback(
    (_: MouseEvent | TouchEvent, node: Node, dragged: Node[]) => {
      isDraggingRef.current = false;
      if (dragged.length > 1) {
        const updates: Record<string, { x: number; y: number }> = {};
        for (const rn of renderNodesRef.current) {
          if (rn.type === 'table' && (rn.selected || dragged.some((d) => d.id === rn.id))) updates[rn.id] = rn.position;
        }
        for (const d of dragged) {
          if (d.type !== 'domainGroup') continue;
          const groupId = d.id.slice(GROUP_PREFIX.length);
          for (const t of tables) {
            if (t.groupId !== groupId) continue;
            const rn = renderNodesRef.current.find((n) => n.id === t.id);
            if (rn) updates[t.id] = rn.position;
          }
        }
        if (Object.keys(updates).length) setTablePositions(updates, true);
        return;
      }
      if (node.type === 'table') {
        setTablePosition(node.id, node.position, true);
      } else if (node.type === 'domainGroup') {
        const groupId = node.id.slice(GROUP_PREFIX.length);
        const memberIds = new Set(tables.filter((t) => t.groupId === groupId).map((t) => t.id));
        const updates: Record<string, { x: number; y: number }> = {};
        for (const rn of renderNodesRef.current) {
          if (memberIds.has(rn.id)) updates[rn.id] = rn.position;
        }
        if (Object.keys(updates).length) setTablePositions(updates, true);
      }
    },
    [setTablePosition, setTablePositions, tables],
  );

  return (
    <div ref={containerRef} className="relative h-full w-full bg-canvas dark:bg-gray-950">
      <ReactFlow
        nodes={renderNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={setInstance}
        onConnect={handleConnect}
        onNodesChange={handleNodesChange}
        onNodeClick={handleNodeClick}
        onEdgeClick={handleEdgeClick}
        onPaneClick={handlePaneClick}
        onNodeContextMenu={handleNodeContextMenu}
        onEdgeContextMenu={handleEdgeContextMenu}
        onSelectionChange={handleSelectionChange}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        nodesDraggable={interactionMode === 'select' && !readOnly}
        nodesConnectable={interactionMode === 'select' && !readOnly}
        selectionOnDrag={interactionMode === 'select'}
        selectionKeyCode={interactionMode === 'select' ? 'Shift' : null}
        selectionMode={SelectionMode.Partial}
        panOnDrag={interactionMode === 'select' ? [1, 2] : true}
        panActivationKeyCode="Space"
        minZoom={0.1}
        maxZoom={2}
        onlyRenderVisibleElements={!forceRenderAll}
        elevateEdgesOnSelect
        fitView
      >
        {showGrid && <Background variant={BackgroundVariant.Dots} gap={22} size={1.6} color={theme === 'dark' ? '#27324a' : '#cbd5e1'} />}
        <Controls showInteractive={false} />
        <InteractionModeToggle />
        <ToastPanel />
      </ReactFlow>
      {tourOpen && <ErTourOverlay />}
      {menu && <ExplanationContextMenu {...menu} documentation={documentation} onClose={() => setMenu(null)} />}
    </div>
  );
}

export default function DiagramCanvas({ containerRef }: { containerRef: React.RefObject<HTMLDivElement> }) {
  return (
    <ReactFlowProvider>
      <FlowInner containerRef={containerRef} />
    </ReactFlowProvider>
  );
}
